import * as React from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface IconButtonProps
  extends Omit<ButtonProps, "aria-label" | "title"> {
  /**
   * What the button does. It is both the button's accessible name and the
   * tooltip shown on hover and keyboard focus, one string so the two cannot
   * come to say different things (Jira NAV-228).
   */
  label: string;
}

/**
 * A button that is only an icon. A sighted user reads what it does off its
 * tooltip and a screen reader off its name, and both come from `label`.
 *
 * Every icon-only button in the app is one of these: `iconButtons.test.ts`
 * fails on a `<Button>` or `<button>` whose content is only an icon.
 *
 * **The tooltip's trigger is a span around the button**, not the button: a
 * disabled button receives no pointer events, and a greyed-out icon is exactly
 * the one whose meaning a reader wants to check. Focus still opens it, since
 * the button's focus bubbles to the span. The span always wraps, so the button
 * is not remounted — and does not lose focus — when it is disabled and
 * re-enabled around a save.
 *
 * It carries its own `TooltipProvider`, so it renders wherever it is mounted —
 * a unit test or a portal — without relying on the app root's. The tooltip is
 * portalled so a table's or a card's overflow never clips it.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, size = "icon", variant = "ghost", ...props }, ref) => (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0">
            <Button
              ref={ref}
              size={size}
              variant={variant}
              aria-label={label}
              {...props}
            />
          </span>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>{label}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  ),
);
IconButton.displayName = "IconButton";
